import React from "react";
import { LightbulbIcon, SparklesIcon, InfoIcon, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CognitiveDistribution } from "../../lib/assessmentService";
import { useAuth } from "../../context/AuthContext";
import { updateChallengeProgress } from "../../lib/supabase";
import toast from "react-hot-toast";

interface AssessmentFormProps {
  formData: {
    mataPelajaran: string;
    jenjangPendidikan: string;
    kelas: string;
    topik: string;
    tujuanPembelajaran: string;
    tipePertanyaan: string;
    kriteriaTambahan: string;
    // Enhanced fields
    jumlahSoal: number;
    tingkatKesulitan: string;
    distribusiKognitif: CognitiveDistribution;
    includeAnswerKey: boolean;
    includeRubric: boolean;
    aloksiWaktu: number;
  };
  handleInputChange: (
    field: string,
    value: string | number | boolean | CognitiveDistribution
  ) => void;
  isFormValid: boolean;
  isGenerating: boolean;
  progress: number;
  handleGenerateNow: () => void;
  handleEnhancePrompt: () => void;
}

export const AssessmentForm: React.FC<AssessmentFormProps> = ({
  formData,
  handleInputChange,
  isFormValid,
  isGenerating,
  handleGenerateNow,
  handleEnhancePrompt,
}) => {

  const { user } = useAuth();

  const handleEnhanceClick = async () => {
    handleEnhancePrompt();

    if (!user?.id) {
      console.error("User tidak ditemukan untuk gamifikasi");
      return;
    }

    try {
      updateChallengeProgress(user.id, "enhance_prompt").then(result => {
        if (result.success && result.data?.challenge_completed && result.data.points_awarded > 0) {
          toast.success(`Tantangan Selesai: +${result.data.points_awarded} Poin!`);
        } else if (result.error) {
          console.error("Gagal update challenge 'enhance_prompt':", result.error);
        }
      });

    } catch (error) {
      console.error("Error memicu challenge 'enhance_prompt':", error);
    }
  };

  const getKelasOptions = () => {
    const jenjang = formData.jenjangPendidikan;
    if (jenjang === "tk") return ["TK-A", "TK-B"];
    if (jenjang === "paud") return ["PAUD-A", "PAUD-B"];
    if (jenjang === "sd")
      return ["Kelas 1", "Kelas 2", "Kelas 3", "Kelas 4", "Kelas 5", "Kelas 6"];
    if (jenjang === "smp") return ["Kelas 7", "Kelas 8", "Kelas 9"];
    if (jenjang === "sma" || jenjang === "smk")
      return ["Kelas 10", "Kelas 11", "Kelas 12"];
    return [];
  };
  return (
    <div className="space-y-6">
      {/* Mata Pelajaran */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Mata Pelajaran
        </label>
        <Input
          value={formData.mataPelajaran}
          onChange={e => handleInputChange("mataPelajaran", e.target.value)}
          placeholder="Ketik mata pelajaran"
          className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white"
        />
      </div>

      {/* Jenjang Pendidikan */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Jenjang Pendidikan
        </label>
        <Select
          value={formData.jenjangPendidikan}
          onValueChange={value => handleInputChange("jenjangPendidikan", value)}
        >
          <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white">
            <SelectValue placeholder="Pilih jenjang Pendidikan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paud">
              PAUD (Pendidikan Anak Usia Dini)
            </SelectItem>
            <SelectItem value="tk">TK (Taman Kanak-kanak)</SelectItem>
            <SelectItem value="sd">SD (Sekolah Dasar)</SelectItem>
            <SelectItem value="smp">SMP (Sekolah Menengah Pertama)</SelectItem>
            <SelectItem value="sma">SMA (Sekolah Menengah Atas)</SelectItem>
            <SelectItem value="smk">SMK (Sekolah Menengah Kejuruan)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Kelas (dependent on Jenjang) */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Kelas
        </label>
        <Select
          value={formData.kelas}
          onValueChange={value => handleInputChange("kelas", value)}
          disabled={!formData.jenjangPendidikan}
        >
          <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white disabled:opacity-60 disabled:cursor-not-allowed">
            <SelectValue
              placeholder={
                formData.jenjangPendidikan
                  ? "Pilih Kelas"
                  : "Pilih Jenjang terlebih dahulu"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {getKelasOptions().map(opt => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Topik */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Topik
        </label>
        <Textarea
          value={formData.topik}
          onChange={e => handleInputChange("topik", e.target.value)}
          placeholder="Kerajaan Hindu di Indonesia"
          className="min-h-[80px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white"
        />
      </div>

      {/* Pilih Tipe Pertanyaan */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Pilih Tipe Pertanyaan
        </label>
        <Select
          value={formData.tipePertanyaan}
          onValueChange={value => handleInputChange("tipePertanyaan", value)}
        >
          <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white">
            <SelectValue placeholder="Pilih tipe pertanyaan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pilihan-ganda">Pilihan Ganda</SelectItem>
            <SelectItem value="uraian">Essay/Uraian</SelectItem>
            <SelectItem value="benar-salah">Benar/Salah</SelectItem>
            <SelectItem value="menjodohkan">Menjodohkan</SelectItem>
            <SelectItem value="campuran">Campuran</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tujuan Pembelajaran */}
      <div className="relative overflow-hidden bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-purple-100/40 via-pink-100/30 to-transparent rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg shadow-purple-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                Tujuan Pembelajaran
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Jelaskan tujuan pembelajaran untuk kelas Anda
              </p>
            </div>
          </div>
          <Textarea
            value={formData.tujuanPembelajaran}
            onChange={e =>
              handleInputChange("tujuanPembelajaran", e.target.value)
            }
            placeholder="Siswa dapat menganalisis perkembangan kerajaan Hindu di Indonesia dan pengaruhnya terhadap budaya lokal"
            className="min-h-[80px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white"
          />
        </div>

        {/* Kriteria Tambahan */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-[#373e46] mt-5">
            Kriteria Tambahan (Wajib)
          </label>
          <Textarea
            value={formData.kriteriaTambahan}
            onChange={e =>
              handleInputChange("kriteriaTambahan", e.target.value)
            }
            placeholder="Siswa belum pernah mendalam tentang kerajaan Hindu; saya bisa pakai metode diskusi kelompok; media gambar dan video; fokus pada analisis kritis."
            className="min-h-[100px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white"
          />
        </div>
      </div>

      {/* Enhance Prompt Button */}
      <div className="flex justify-start">
        <Button
          onClick={handleEnhanceClick}
          disabled={!isFormValid}
          variant="outline"
          className="flex items-center gap-2 text-[#6b2cfe] border-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white rounded-xl px-4 py-2 transition-all duration-300"
        >
          <LightbulbIcon className="w-4 h-4" />
          Enhance Prompt
        </Button>
      </div>

      {/* Enhanced Assessment Settings */}
      <div className="bg-gray-50 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <InfoIcon className="w-5 h-5 text-[#6b2cfe]" />
          <h3 className="text-lg font-semibold text-[#373e46]">
            Pengaturan Assessment
          </h3>
        </div>

        {/* Jumlah Soal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#373e46]">
              Jumlah Soal
            </label>
            <Input
              type="number"
              value={formData.jumlahSoal}
              onChange={e =>
                handleInputChange("jumlahSoal", parseInt(e.target.value) || 10)
              }
              placeholder="10"
              min="1"
              max="50"
              className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white"
            />
            <p className="text-xs text-gray-500">
              Rekomendasi: PG (20), Essay (5), Benar/Salah (15)
            </p>
          </div>

          {/* Alokasi Waktu */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#373e46]">
              Alokasi Waktu (menit)
            </label>
            <Input
              type="number"
              value={formData.aloksiWaktu}
              onChange={e =>
                handleInputChange("aloksiWaktu", parseInt(e.target.value) || 60)
              }
              placeholder="60"
              min="10"
              max="180"
              className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white"
            />
            <p className="text-xs text-gray-500">Estimasi waktu pengerjaan</p>
          </div>
        </div>

        {/* Tingkat Kesulitan */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[#373e46]">
            Tingkat Kesulitan
          </label>
          <Select
            value={formData.tingkatKesulitan}
            onValueChange={value =>
              handleInputChange("tingkatKesulitan", value)
            }
          >
            <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white">
              <SelectValue placeholder="Pilih tingkat kesulitan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mudah">
                Mudah - Fokus pada pemahaman dasar
              </SelectItem>
              <SelectItem value="sedang">
                Sedang - Kombinasi dasar dan aplikasi
              </SelectItem>
              <SelectItem value="sulit">
                Sulit - Analisis dan evaluasi tingkat tinggi
              </SelectItem>
              <SelectItem value="campuran">
                Campuran - Berbagai tingkat kesulitan
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Distribusi Kognitif */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-[#373e46]">
            Distribusi Level Kognitif (Taksonomi Bloom)
          </label>
          <div className="space-y-4">
            {/* C1-C2 Level */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#373e46]">
                  Level C1-C2 (Mengingat & Memahami)
                </span>
                <span className="text-sm font-medium text-[#6b2cfe]">
                  {formData.distribusiKognitif.c1c2}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.distribusiKognitif.c1c2}
                onChange={e => {
                  const newValue = parseInt(e.target.value);
                  const remaining = 100 - newValue;
                  const c3Ratio =
                    formData.distribusiKognitif.c3 /
                      (formData.distribusiKognitif.c3 +
                        formData.distribusiKognitif.c4c5c6) || 0.5;
                  const newC3 = Math.round(remaining * c3Ratio);
                  const newC4c5c6 = remaining - newC3;

                  handleInputChange("distribusiKognitif", {
                    c1c2: newValue,
                    c3: newC3,
                    c4c5c6: newC4c5c6,
                  });
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            {/* C3 Level */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#373e46]">
                  Level C3 (Mengaplikasikan)
                </span>
                <span className="text-sm font-medium text-[#6b2cfe]">
                  {formData.distribusiKognitif.c3}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max={100 - formData.distribusiKognitif.c1c2}
                value={formData.distribusiKognitif.c3}
                onChange={e => {
                  const newValue = parseInt(e.target.value);
                  const newC4c5c6 =
                    100 - formData.distribusiKognitif.c1c2 - newValue;

                  handleInputChange("distribusiKognitif", {
                    c1c2: formData.distribusiKognitif.c1c2,
                    c3: newValue,
                    c4c5c6: newC4c5c6,
                  });
                }}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>

            {/* C4-C6 Level */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#373e46]">
                  Level C4-C6 (Menganalisis, Mengevaluasi, Mencipta)
                </span>
                <span className="text-sm font-medium text-[#6b2cfe]">
                  {formData.distribusiKognitif.c4c5c6}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-lg">
                <div
                  className="h-2 bg-[#6b2cfe] rounded-lg transition-all duration-300"
                  style={{ width: `${formData.distribusiKognitif.c4c5c6}%` }}
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Total:{" "}
            {formData.distribusiKognitif.c1c2 +
              formData.distribusiKognitif.c3 +
              formData.distribusiKognitif.c4c5c6}
            %
          </p>
        </div>

        {/* Additional Options */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-[#373e46]">
            Opsi Tambahan
          </label>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.includeAnswerKey}
                onChange={e =>
                  handleInputChange("includeAnswerKey", e.target.checked)
                }
                className="w-4 h-4 text-[#6b2cfe] bg-gray-100 border-gray-300 rounded focus:ring-[#6b2cfe] focus:ring-2"
              />
              <span className="text-sm text-[#373e46]">
                Sertakan Kunci Jawaban
              </span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.includeRubric}
                onChange={e =>
                  handleInputChange("includeRubric", e.target.checked)
                }
                className="w-4 h-4 text-[#6b2cfe] bg-gray-100 border-gray-300 rounded focus:ring-[#6b2cfe] focus:ring-2"
              />
              <span className="text-sm text-[#373e46]">
                Sertakan Rubrik Penilaian (untuk Essay)
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="flex justify-center pt-6">
        <Button
          onClick={handleGenerateNow}
          disabled={!isFormValid || isGenerating}
          className={`w-full h-12 font-semibold rounded-xl transition-all duration-300 ${
            isFormValid && !isGenerating
              ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full">
              <div className="flex items-center gap-3">
                <SparklesIcon className="w-5 h-5 animate-pulse" />
                <span>Generate Hasil</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <SparklesIcon className="w-5 h-5" />
              Generate Now
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};
